import { Component } from '@theme/component';
import { fetchConfig, resetShimmer } from '@theme/utilities';
import { cartMutationGate, cartSectionMatches } from '@theme/zentus-cart-gate';
import { buildSectionSelector, morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #pendingQuantities = new Map();
  #quantityTimer;

  #scheduleQuantityChange = (event) => {
    if (!(event.target instanceof Node) || !this.contains(event.target) || cartMutationGate.blocked) return;
    const { quantity, cartLine: line } = event.detail;
    const key = event.target.closest('[data-key]')?.dataset.key;
    if (!line || !key) return;
    // Capture identity now: a later morph can reuse this element for another line.
    this.#pendingQuantities.set(key, { quantity, line, key });
    clearTimeout(this.#quantityTimer);
    this.#quantityTimer = setTimeout(() => this.flushPendingQuantityChanges(), 300);
  };

  flushPendingQuantityChanges() {
    clearTimeout(this.#quantityTimer);
    const pending = [...this.#pendingQuantities.values()];
    this.#pendingQuantities.clear();
    return Promise.all(pending.map(config => this.#onQuantityChange(config)));
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#scheduleQuantityChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#scheduleQuantityChange);
    clearTimeout(this.#quantityTimer);
    this.#pendingQuantities.clear();
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {{quantity:number, line:number, key:string}} config - Captured line update.
   */
  #onQuantityChange({ quantity, line, key }) {

    if (quantity === 0) {
      return this.updateQuantity({ line, key, quantity: 0, action: 'clear' });
    }

    const update = this.updateQuantity({
      line,
      key,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return update;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
    return update;
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const key = this.refs.cartItemRows[line - 1]?.dataset.key;
    this.flushPendingQuantityChanges();
    // Keep the last confirmed rows until Shopify returns the native section.
    return this.updateQuantity({ line, key, quantity: 0, action: 'clear' });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const key = config.key || this.refs.cartItemRows[config.line - 1]?.dataset.key;
    if (!key || cartMutationGate.blocked) return Promise.resolve();
    return cartMutationGate.run(() => this.#updateQuantity({ ...config, key })).catch(error => {
      // A preceding uncertain mutation already presents the cart-wide recovery link.
      if (!cartMutationGate.blocked) console.error(error);
    });
  }

  #updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      id: config.key,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();
    this.refs[`cartItemErrorContainer-${line}`]?.classList.add('hidden');

    return fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then(async (response) => {
        const parsedResponseText = JSON.parse(await response.text());
        if (parsedResponseText?.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }
        const sectionHTML = parsedResponseText?.sections?.[this.sectionId];
        if (!response.ok || !Array.isArray(parsedResponseText?.items) ||
          !Number.isInteger(parsedResponseText.item_count) || parsedResponseText.item_count < 0 ||
          typeof sectionHTML !== 'string' || !sectionHTML.trim()) {
          throw new Error('Cart update could not be confirmed');
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        const sectionSelector = buildSectionSelector(this.sectionId);
        const newSection = newSectionHTML.getElementById(sectionSelector);
        const currentSection = document.getElementById(sectionSelector);
        const newCart = newSection?.querySelector('cart-items-component');
        if (!newSection || !currentSection) throw new Error('Cart section wrapper could not be confirmed');
        if (this.isDrawer) {
          const currentTarget = this.closest('[data-hydration-key]');
          const newTarget = newCart?.closest('[data-hydration-key]');
          const hydrationKey = currentTarget?.getAttribute('data-hydration-key');
          if (!hydrationKey || newTarget?.getAttribute('data-hydration-key') !== hydrationKey ||
            !currentSection.contains(currentTarget) || !newSection.contains(newTarget)) {
            throw new Error('Cart drawer hydration target could not be confirmed');
          }
        }
        const newCartItemCount = parsedResponseText.item_count;
        const countText = newCart?.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
        const rows = Array.from(newCart?.querySelectorAll('tr[ref="cartItemRows[]"]') ?? []);
        const items = parsedResponseText.items;
        const confirmsRows = rows.length === items.length && rows.every((row, index) =>
          typeof items[index]?.key === 'string' && items[index].key.length > 0 &&
          row.dataset.key === items[index].key && Number.isInteger(items[index].quantity) && items[index].quantity > 0
        );
        const confirmsItems = newCartItemCount > 0 && countText && /^\d+$/.test(countText) &&
          Number(countText) === newCartItemCount && items.length > 0 && confirmsRows &&
          items.reduce((total, item) => total + item.quantity, 0) === newCartItemCount;
        const confirmsEmpty = newCartItemCount === 0 && items.length === 0 && rows.length === 0 &&
          newCart?.querySelector('.cart-items__empty-button');
        if (newCart?.dataset.sectionId !== this.sectionId || (!confirmsItems && !confirmsEmpty) || !cartSectionMatches(parsedResponseText, newCart)) {
          throw new Error('Cart section could not be confirmed');
        }

        // Rendering can reject asynchronously; announce success only after native content is updated.
        await morphSection(this.sectionId, sectionHTML, this.isDrawer ? 'hydration' : 'full');

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );


        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
        // A failed response can follow a committed write. Keep stale indices and
        // checkout unavailable until the shopper reloads the verified server cart.
        cartMutationGate.block();
        this.#handleCartError(line, { errors: Theme.translations.cart_update_error });
      })
      .finally(() => {
        resetShimmer(this);
        this.#updateCartQuantitySelectorButtonStates();
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = typeof parsedResponseText.errors === 'string'
      ? parsedResponseText.errors
      : Theme.translations.cart_update_error;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      if (event.detail.renderedSections?.includes(this.sectionId)) return;
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;
    if (event.detail.data.renderedSections?.includes(this.sectionId)) {
      this.#updateCartQuantitySelectorButtonStates();
      return;
    }

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}
